import React, { createContext, useContext, useState, useEffect } from 'react'
import { supabase, signUp, signIn, signOut, getCurrentUser, getSession } from '../lib/supabase'

const AuthContext = createContext()

export function useAuth() {
  return useContext(AuthContext)
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [session, setSession] = useState(null)

  // Check for existing session on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const session = await getSession()
        setSession(session)
        
        if (session?.user) {
          setUser(session.user)
        }
      } catch (error) {
        console.error('Error checking auth session:', error)
      } finally {
        setLoading(false)
      }
    }

    initializeAuth()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session)
        setUser(session?.user ?? null)
        setLoading(false)
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  // Sign up function
  const signup = async (email, password) => {
    try {
      const data = await signUp(email, password)
      return { success: true, data }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  // Sign in function
  const login = async (email, password) => {
    try {
      const data = await signIn(email, password)
      return { success: true, data }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  // Sign out function
  const logout = async () => {
    try {
      await signOut()
      setUser(null)
      setSession(null)
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const value = {
    user,
    session,
    loading,
    signup,
    login,
    logout,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  )
}
